import AccommodationIndividualRoomTile from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTile'
import AccommodationIndividualRoomTileHeader from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileHeader'
import AccommodationIndividualRoomTileImage from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileImage'
import AccommodationIndividualRoomTileDetails from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileDetails'
import AccommodationIndividualRoomTilePricing from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTilePricing'
import { OfferPageActions, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import React, { useCallback, useContext, useMemo } from 'react'
import WalledContent from 'components/Common/WalledContent'
import { isAvailableForPurchase, offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import TextButton from 'LuxKit/Button/TextButton'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import Caption from 'LuxKit/Typography/Caption'
import { pluralizeToString } from 'lib/string/pluralize'
import { rem } from 'polished'
import styled from 'styled-components'
import ModalContext from 'contexts/ModalContext'
import Group from 'components/utils/Group'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import Clickable from 'components/Common/Clickable'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import MultipleRoomsBookingPriceDetails from 'components/OfferPage/MultipleRoomsBooking/MultipleRoomsBookingPriceDetails'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import AccommodationAmenitiesAndPoliciesModal from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesModal'
import IndividualRoomPackageSelectionModal from './IndividualRoomPackageSelectionModal'
import MultipleRoomsInclusions from 'components/OfferPage/MultipleRoomsBooking/MultipleRoomsInclusions'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import Sheet from 'components/utils/Sheet'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import GeoContext from 'contexts/geoContext'

const LoyaltyPointsWrapper = styled.div`
  padding-block: ${rem(2)}; // To compensate for the loyalty coin which protrudes a couple of pixels outside of its container

  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  packageNumber?: number
  room: OfferPageRoom
  rooms: Array<OfferPageRoom>
  offer: App.HotelOffer
  packageView?: App.OfferPackageOptionView
  checkInDate: string | undefined
  checkOutDate: string | undefined
  flightsEnabled: boolean
  isFlightsSelected: boolean
  airports?: Array<App.Airport>
  isFetching: boolean
  availableForPurchase: boolean
  availablePackages: Array<App.OfferPackageOptionView>
}

function MultiIndividualRoomRoom({
  room,
  rooms,
  offer,
  packageView,
  packageNumber,
  checkInDate,
  checkOutDate,
  flightsEnabled,
  isFlightsSelected,
  airports,
  isFetching,
  availableForPurchase,
  availablePackages,
}: Props) {
  const { totalFlightPrice } = useOfferPageFlightPrice()

  const flightDestination = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const roomPax = sumUpOccupancies([room])
  const totalPax = sumUpOccupancies(rooms)
  const flightPriceProportion = roomPax / totalPax

  const flightPrice = totalFlightPrice ? totalFlightPrice * flightPriceProportion : undefined

  const validCheckinCheckout = !!(checkInDate && checkOutDate)

  const hasFlights = flightsEnabled && isFlightsSelected

  const showModal = useContext(ModalContext)

  const offerPageDispatch = useOfferPageDispatch()

  const isDesktopLayout = useScreenSizeUp('tablet')

  const handleCheckAvailabilityClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const handleChangeDatesClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch])

  const showRoomDetails = useCallback(() => {
    if (packageView) {
      showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={packageView.package} />)
    }
  }, [packageView, showModal])

  const onClickChangeRoom = useCallback(() => {
    showModal(<IndividualRoomPackageSelectionModal
      roomId={room.id}
      availablePackages={availablePackages}
    />)
  }, [availablePackages, room.id, showModal])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView: packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView: packageView, flightPrice, skip: !hasFlights }),
  ], [hasFlights, offer, packageView, flightPrice, currentRegionCode])

  const onClickLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!packageView?.availableRooms && !!checkInDate)

  // After the rates are fetched, there's a small delay before the package autoselection runs (in an effect).
  // This causes the tile to briefly show a "No room available" message. Adding a new "room not selected yet" state
  // would be a pain, so let's just bridge the time gap between rates loading and rooms being selected by extending
  // the loading state by 200 milliseconds.
  const showAsFetchingWhilePackageMissing = useDelayedTogglingBoolean(isFetching, { offDelay: 200 }) && !packageView

  if (!packageView) {
    if (showAsFetchingWhilePackageMissing) {
      return <Sheet padding="20 0" background="neutral-eight">
        <LoadingIndicator inline />
      </Sheet>
    } else if (availablePackages.every(view => !allRoomsOccupantsFitRoomCapacity(room, view.package.roomRate.capacities, offer))) {
      // No availability due to occupants exceeding capacity for all room types
      return <AccommodationIndividualRoomTile>
        <AccommodationIndividualRoomTileHeader room={room} heading="No room available" packageNumber={packageNumber} offer={offer} />
        <AccommodationIndividualRoomTileDetails>
          <MessageBanner
            kind="warning"
            density="compact"
          >
            No room available, please <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>revise number of guests</TextLink>
          </MessageBanner>
        </AccommodationIndividualRoomTileDetails>
      </AccommodationIndividualRoomTile>
    } else {
      // No availability due to dates
      return <AccommodationIndividualRoomTile>
        <AccommodationIndividualRoomTileHeader room={room} heading="No room available" packageNumber={packageNumber} offer={offer} />
        <AccommodationIndividualRoomTileDetails>
          <MessageBanner
            kind="warning"
            density="compact"
          >
            No room available, please <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>try different dates</TextLink> or <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>revise number of guests</TextLink>
          </MessageBanner>
        </AccommodationIndividualRoomTileDetails>
      </AccommodationIndividualRoomTile>
    }
  }

  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = packageView.package?.ignoreDynamicCancellationPolicy ?? false
  const canSelectRoom = !!packageView.availableRooms && packageView.capacityValid
  const canChangeRoom = availablePackages.length > 1

  return <AccommodationIndividualRoomTile roomPackage={packageView.package}>
    <AccommodationIndividualRoomTileHeader
      room={room}
      heading={packageView.package.roomType.name}
      packageNumber={packageNumber}
      offer={offer}
      onTitleClick={showRoomDetails}
    />
    <AccommodationIndividualRoomTileImage roomPackage={packageView.package} offer={offer} />
    <AccommodationIndividualRoomTileDetails>
      <LabelGroup>
        {checkInDate && !croPopularOptionBookingRemaining && <RoomsRemainingLabel roomCount={packageView.availableRooms ?? 0} threshold="rate" />}
        {checkInDate && croPopularOptionBookingRemaining && !!packageView.availableRooms && <OfferUrgencyRoomCount availableRoomCount={packageView.availableRooms} /> }
        {!!offer.stats.booked && <BookingsCountLabel bookings={offer.stats.booked} threshold="room" />}
      </LabelGroup>
      <div>
        <Clickable onClick={showRoomDetails}>
          <AccommodationRoomDetailsList roomPackage={packageView.package} />
        </Clickable>
      </div>
      <div>
        {offer.type === OFFER_TYPE_ALWAYS_ON && <TAOCancellationPolicies
          offerId={offer.id}
          useDynamicCancellationPolicies={useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy}
          timezoneOffset={offer.property?.timezoneOffset}
          uniqueKey={packageView.package.uniqueKey}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          occupants={[room]}
          cancellationPolicy={packageView.package?.roomRate?.cancellationPolicy}
          offerType={offer.type}
        />}
        {offer.type === OFFER_TYPE_HOTEL && availableForPurchase && <FlashCancellationPolicies
          checkInDate={checkInDate}
          propertyTimezoneOffset={offer.property?.timezoneOffset}
          cancellationPolicy={packageView.package.roomRate?.cancellationPolicy}
        />}
      </div>
      <LoyaltyPointsWrapper>
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={pointsEarnCalculationRequests}
          calculationType={(!checkInDate || isFlightsSelected) ? 'estimate' : 'exact'}
        />
      </LoyaltyPointsWrapper>
      <MultipleRoomsInclusions offer={offer} packageOption={packageView} />
    </AccommodationIndividualRoomTileDetails>
    <WalledContent
      enforced={offer.walledGarden}
      wall={<AccommodationIndividualRoomTilePricing direction="horizontal" desktopDirection="vertical" gap={8} horizontalAlign="stretch">
        <TextButton
          kind="primary"
          size="medium"
          onClick={onClickLogin}
          fit="full-width"
        >
          Log in to view prices
        </TextButton>
        <Group direction="vertical" gap={8} verticalAlign="end" horizontalAlign="center">
          <TextButton fit="full-width" variant="dark" kind="secondary" size="medium" startIcon={<LineEditAltIcon />} onClick={onClickChangeRoom} disabled={!canChangeRoom}>Change room</TextButton>
          <Caption variant="medium" colour="neutral-three">
            {canChangeRoom ? `${pluralizeToString('room option', availablePackages.length)} available` : 'No additional options'}
          </Caption>
        </Group>
      </AccommodationIndividualRoomTilePricing>}
    >
      <AccommodationIndividualRoomTilePricing direction="horizontal" gap={8} horizontalAlign="stretch" desktopDirection="vertical" desktopVerticalAlign="end">
        {!isFetching && validCheckinCheckout && !canSelectRoom && <>
          {!packageView.capacityValid && <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading="This room cannot accommodate your number of guests"
          >
            Choose another room or <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>add more rooms</TextLink>
          </MessageBanner>}
          {!packageView.availableRooms && packageView.capacityValid && <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading="This room is sold out on your dates or max number of guests exceeded"
          >
            Choose another room or <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>try different dates</TextLink>
          </MessageBanner>}
        </>}
        <MultipleRoomsBookingPriceDetails
          flightDestination={flightDestination}
          room={room}
          packageOption={packageView}
          isFetching={isFetching}
          hasFlights={hasFlights}
          buyNowBookLater={false}
          showPoints={isDesktopLayout}
          flightPrice={flightPrice}
        />
        <Group direction="vertical" gap={8} verticalAlign="end" horizontalAlign="center">
          <TextButton fit="full-width" variant="dark" kind="secondary" size="medium" startIcon={<LineEditAltIcon />} onClick={onClickChangeRoom} disabled={!canChangeRoom}>Change room</TextButton>
          <Caption variant="medium" colour="neutral-three">
            {canChangeRoom ? `${pluralizeToString('room option', availablePackages.length)} available` : 'No additional options'}
          </Caption>
        </Group>
      </AccommodationIndividualRoomTilePricing>
    </WalledContent>
  </AccommodationIndividualRoomTile>
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'flightsEnabled'
  | 'isFlightsSelected'
  | 'airports'
  | 'availableForPurchase'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  flightsEnabled: offerFlightsEnabled(state, appState),
  isFlightsSelected: state.flights,
  airports: appState.geo.airports,
  availableForPurchase: isAvailableForPurchase(state, appState),
}), MultiIndividualRoomRoom)
